import { useMemo } from "react";
import { highlighter } from "@/shared/shiki";

/**
 * Mime → shiki lang. Returns:
 *   - string lang  → render with shiki via CodeFileReader
 *   - 'plain'      → render in a plain <pre> via PlainTextReader
 *   - 'markdown'   → authoritative markdown; caller must skip code detection
 *   - null         → unknown / not set; caller falls back to extension + sniff
 */
export function ctMimeToLang(ct: string | undefined): string | null {
  if (!ct) return null;
  const base = ct.split(";")[0]?.trim().toLowerCase() ?? "";
  switch (base) {
    case "text/markdown":
    case "text/x-markdown":
      return "markdown";
    case "text/plain":
      return "plain";
    case "text/x-typescript":
    case "application/typescript":
      return "ts";
    case "text/x-tsx":
      return "tsx";
    case "text/javascript":
    case "application/javascript":
      return "js";
    case "text/jsx":
      return "jsx";
    case "text/html":
      return "html";
    case "text/css":
      return "css";
    case "text/xml":
    case "application/xml":
      return "xml";
    case "application/json":
    case "application/schema+json":
      return "json";
    case "application/yaml":
    case "text/yaml":
    case "text/x-yaml":
    case "application/x-openapi+yaml":
      return "yaml";
    case "application/toml":
    case "text/x-toml":
      return "toml";
    case "text/x-python":
    case "application/x-python":
      return "python";
    case "text/x-rust":
      return "rust";
    case "text/x-go":
      return "go";
    case "text/x-shellscript":
    case "application/x-sh":
      return "bash";
    case "text/x-sql":
    case "application/sql":
      return "sql";
    case "text/x-dockerfile":
      return "dockerfile";
    default:
      return null;
  }
}

/**
 * Short human badge label for a non-markdown content-type. Returns null for
 * markdown or when the caller should show nothing.
 */
export function ctMimeToBadgeLabel(ct: string | undefined): string | null {
  const lang = ctMimeToLang(ct);
  if (lang === null) {
    // Unknown mime — try to derive a short subtype (ts/x-foo → foo).
    if (!ct) return null;
    const base = ct.split(";")[0]?.trim().toLowerCase() ?? "";
    const sub = base.split("/")[1];
    if (!sub) return null;
    const cleaned = sub.replace(/^x-/, "").replace(/\+json$/, "").replace(/\+yaml$/, "");
    return cleaned.length <= 12 ? cleaned : null;
  }
  if (lang === "markdown") return null;
  if (lang === "plain") return "text";
  return lang;
}

/** Render body in a plain <pre> without shiki — for `text/plain` logs etc. */
export function PlainTextReader({ body }: { body: string }) {
  return (
    <div className="prose-rtfm">
      <pre className="whitespace-pre-wrap break-words">
        <code>{body}</code>
      </pre>
    </div>
  );
}

/** Map file extension → shiki language id. Extend as needed. */
const EXT_TO_LANG: Record<string, string> = {
  ts: "ts",
  tsx: "tsx",
  js: "js",
  jsx: "jsx",
  mjs: "js",
  cjs: "js",
  json: "json",
  yml: "yaml",
  yaml: "yaml",
  toml: "toml",
  sh: "bash",
  bash: "bash",
  zsh: "bash",
  py: "python",
  rb: "ruby",
  rs: "rust",
  go: "go",
  java: "java",
  kt: "kotlin",
  swift: "swift",
  c: "c",
  h: "c",
  cpp: "cpp",
  hpp: "cpp",
  cs: "cpp",
  php: "php",
  sql: "sql",
  html: "html",
  htm: "html",
  xml: "xml",
  css: "css",
  scss: "scss",
  graphql: "graphql",
  gql: "graphql",
  dockerfile: "dockerfile",
  lua: "lua",
  make: "make",
};

/** Detect language from slug extension only. */
export function detectCodeLang(slug: string): string | null {
  const leaf = slug.split("/").pop() ?? slug;
  if (leaf.toLowerCase() === "dockerfile") return "dockerfile";
  if (leaf.toLowerCase() === "makefile") return "make";
  const m = leaf.match(/\.([a-z0-9]+)$/i);
  const ext = m?.[1];
  if (!ext) return null;
  return EXT_TO_LANG[ext.toLowerCase()] ?? null;
}

/**
 * Heuristic content sniff when the slug has no extension.
 * Runs a set of ordered regexes against the first ~2 KiB of the body.
 * Returns a shiki lang id or null when content looks like prose/markdown.
 */
export function sniffCodeLangFromBody(body: string): string | null {
  const head = body.slice(0, 2048);
  const trimmed = head.trimStart();
  if (!trimmed) return null;

  // Shebang
  const sheb = trimmed.match(/^#!\s*(\S+)(?:\s+(\S+))?/);
  if (sheb) {
    const bin = (sheb[2] ?? sheb[1] ?? "").split("/").pop() ?? "";
    if (bin.includes("python")) return "python";
    if (bin.includes("node")) return "js";
    if (bin.includes("bash") || bin.includes("sh") || bin.includes("zsh")) return "bash";
    if (bin.includes("ruby")) return "ruby";
  }

  // Looks like markdown → bail out (let markdown reader handle it).
  // Headings, lists, or fenced blocks at column 0.
  if (/^(#{1,6}\s|\s*[-*+]\s|\d+\.\s|```)/m.test(head)) return null;

  // XML / HTML
  if (/^\s*<\?xml\b/.test(trimmed)) return "xml";
  if (/^\s*<!DOCTYPE\s+html/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed)) return "html";
  if (/^\s*<\?php\b/.test(trimmed)) return "php";

  // JSON (top-level object or array + balanced-ish)
  if (/^\s*[{[]/.test(trimmed) && /[}\]]\s*$/.test(body.trimEnd())) {
    try {
      JSON.parse(body);
      return "json";
    } catch {
      /* not JSON, continue */
    }
  }

  // YAML (front-matter or top-level key: value pattern)
  if (/^---\s*$/m.test(head) && /^[\w-]+:\s/m.test(head)) return "yaml";

  // TypeScript / JavaScript family.
  // TS-specific markers first (type annotations, interfaces, generics).
  if (/^\s*(import\s+type\b|export\s+type\b|interface\s+\w|type\s+\w+\s*=)/m.test(head)) {
    return "ts";
  }
  if (/:\s*(string|number|boolean|void|any|unknown|Promise<)/.test(head)) return "ts";
  if (/^\s*(import\s|export\s|const\s|let\s|function\s|class\s)/m.test(head)) {
    // Default JS-family to TS — it's a superset and renders JS correctly.
    return "ts";
  }

  // Python
  if (/^\s*(from\s+\w+\s+import|def\s+\w+\(|class\s+\w+[:(]|if\s+__name__\s*==)/m.test(head)) {
    return "python";
  }

  // Rust
  if (/^\s*(fn\s+\w+\s*\(|pub\s+fn\s|use\s+\w+::|impl\s+\w)/m.test(head)) return "rust";

  // Go
  if (/^\s*package\s+\w+\s*$/m.test(head) && /^\s*func\s+\w/m.test(head)) return "go";

  // Shell
  if (/^\s*(#!\/|if\s+\[|for\s+\w+\s+in\s|function\s+\w+\s*\(\)\s*\{)/m.test(head)) {
    return "bash";
  }

  return null;
}

export function CodeFileReader({ body, lang }: { body: string; lang: string }) {
  const html = useMemo(() => {
    try {
      return highlighter.codeToHtml(body, {
        lang,
        themes: { light: "github-light", dark: "github-dark" },
        defaultColor: false,
      });
    } catch {
      return `<pre class="shiki"><code>${escapeHtml(body)}</code></pre>`;
    }
  }, [body, lang]);

  return (
    <div className="prose-rtfm">
      <div
        // biome-ignore lint/security/noDangerouslySetInnerHtml: shiki output is trusted.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
